import { bandColour, bandPaletteColour, type DialModel, type RollupBand } from "@keydials/dial-renderer";
import {
  itemScopeApplies,
  type GeneratedItem,
  type GeneratedStatus,
  type InstanceDetail,
  type ResolvedItem,
  type SectionColour,
  type SectionColourStatus,
  type SegmentRollup,
} from "@keydials/api-client";

const segKey = (sectionKey: string, segmentKey: string) => `${sectionKey}\0${segmentKey}`;

/** Index an instance's current segment statuses by section+segment for O(1) lookup. */
export function statusIndex(detail: InstanceDetail): Map<string, string> {
  const index = new Map<string, string>();
  for (const s of detail.statuses) {
    index.set(segKey(s.sectionKey, s.segmentKey), s.statusKey);
  }
  return index;
}

/**
 * The palette of the axis this dial derives its statuses from (KEY-482), or an empty list when it derives
 * nothing. A derived status key names a colour HERE, not in the dial's default palette — the two are
 * different palettes and only coincide when an author happened to make them so.
 */
export function rollupAxisStatuses(detail: InstanceDetail): GeneratedStatus[] {
  const key = detail.statusRollupDimensionKey;
  if (!key) {
    return [];
  }
  return detail.dimensions?.find((d) => d.key === key)?.palette.statuses ?? [];
}

/**
 * The palette of the axis the dial derives its SECTION edges from (KEY-485), which is the action points'
 * axis unless the dial names a different one. Separate from {@link rollupAxisStatuses} because with the two
 * split, a section's edge and the action points inside it can be reading different palettes, and a status
 * key means nothing without the palette it was picked from.
 */
export function sectionRollupAxisStatuses(detail: InstanceDetail): GeneratedStatus[] {
  const key = detail.sectionStatusRollupDimensionKey || detail.statusRollupDimensionKey;
  if (!key) {
    return [];
  }
  return detail.dimensions?.find((d) => d.key === key)?.palette.statuses ?? [];
}

/**
 * The palette a section's status is picked from and read in: the rolled-up axis's when the dial derives one,
 * otherwise the definition default.
 *
 * A derived edge and a hand-set one are the same edge, so they have to speak the same vocabulary — offering
 * "Met/Not met" on a dial whose sections are otherwise showing Ofsted grades would put two languages on one
 * ring. Rows set before the dial started deriving still hold default-palette keys, which is why
 * {@link sectionStatusOf} reads them with the default behind this one rather than only here.
 */
export function sectionStatusPalette(detail: InstanceDetail, sectionKey?: string): GeneratedStatus[] {
  const own = sectionKey ? sectionOwnPalette(detail, sectionKey) : undefined;
  if (own) {
    return own;
  }
  const axis = sectionRollupAxisStatuses(detail);
  return axis.length > 0 ? axis : detail.statusSet.statuses;
}

/** KEY-795: the colours a section rule starts with — the server's default, mirrored. */
export const DEFAULT_SECTION_PALETTE: SectionColourStatus[] = [
  { key: "missed", name: "Missed", colour: "#dc2626" },
  { key: "not-started", name: "Not started", colour: "#94a3b8" },
  { key: "under-way", name: "Under way", colour: "#f59e0b" },
  { key: "done", name: "Done", colour: "#16a34a" },
];

const asStatus = (s: { key: string; name?: string | null; colour?: string | null }, order = 0): GeneratedStatus => ({
  key: s.key,
  name: s.name ?? s.key,
  colour: s.colour ?? "#94a3b8",
  order,
  allowedRoles: [],
  isTerminal: false,
});

/**
 * KEY-795: a section's OWN colours, when it has them — a rule section always does (its palette, or the default four),
 * and a hand-set section does when its author gave it some. Undefined means the section speaks the dial's vocabulary.
 * They carry no roles: anyone with a role on the section may set one.
 */
export function sectionOwnPalette(detail: InstanceDetail, sectionKey: string): GeneratedStatus[] | undefined {
  const colouring = detail.sections.find((s) => s.key === sectionKey)?.colouring;
  if (colouring?.source === "Rule") {
    return (colouring.palette?.length ? colouring.palette : DEFAULT_SECTION_PALETTE).map((p, i) => asStatus(p, i));
  }
  if (colouring?.source === "HandSet" && colouring.palette?.length) {
    return colouring.palette.map((p, i) => asStatus(p, i));
  }
  return undefined;
}

/**
 * KEY-795: whether a person may set this section's edge by hand — only where the edge would show it: Auto and HandSet
 * sections, and a rule section whose author allows it. The definition's master switch is the caller's to check.
 */
export function sectionTakesHandSet(detail: InstanceDetail, sectionKey: string): boolean {
  const colouring = detail.sections.find((s) => s.key === sectionKey)?.colouring;
  switch (colouring?.source) {
    case undefined:
    case "Auto":
    case "HandSet":
      return true;
    case "Rule":
      return !!colouring.allowHandSet;
    default:
      return false;
  }
}

/** KEY-795: what the server worked out for a section's edge, when the section isn't Auto. */
export function sectionColourOf(detail: InstanceDetail, sectionKey: string): SectionColour | undefined {
  return detail.sectionColours?.find((c) => c.sectionKey === sectionKey);
}

/** The status somebody set by hand on a section, read in its own colours first, then the dial's. */
function handSetStatusOf(detail: InstanceDetail, sectionKey: string): GeneratedStatus | undefined {
  if (!detail.sectionStatusEnabled) {
    return undefined;
  }
  const key = detail.sectionStatuses?.find((s) => s.sectionKey === sectionKey)?.statusKey;
  // The palette the picker offers first, then the default behind it: a row set before this dial started
  // deriving holds a default-palette key, and must not silently stop rendering because the vocabulary moved.
  return key
    ? sectionStatusPalette(detail, sectionKey).find((s) => s.key === key) ?? detail.statusSet.statuses.find((s) => s.key === key)
    : undefined;
}

/**
 * The key of the section a rolling-days dial (My Seven Days) should open on: Today (KEY-775).
 *
 * The server's resolver emits one section per day in order, starting at the dial's own today in its own time
 * zone (KEY-767), so the FIRST `Days` section is Today. Reading it off the detail rather than recomputing a
 * date here keeps the client from disagreeing with the server about which day it is. Null for any dial with no
 * days section, which is what leaves every ordinary dial opening on its overview.
 */
export function todaySectionKeyOf(detail: Pick<InstanceDetail, "sections">): string | null {
  return detail.sections.find((s) => s.kind === "Days")?.key ?? null;
}

/**
 * The status a section carries, in three tiers: the one someone set by hand (KEY-479), else — on a group —
 * the one rolled up from its children (KEY-483), else the one derived from the dial's own scoring axis
 * (KEY-482). Undefined when it has none of them.
 *
 * Hand-set wins deliberately, over both derivations. A derived status is what the section's items (or its
 * children) say; a hand-set one is a person saying otherwise, and a rollup silently overruling that would
 * make the control a lie. Clearing the row deletes it, so the section falls back to whatever is derived —
 * which is what "clear" should mean once something is being derived.
 *
 * The group rollup sits ABOVE the axis derivation only because the two cannot both be present: a group has
 * no items and so no axis, and a dial with an axis has no children. The order states the precedence
 * anyway rather than relying on that staying true.
 *
 * The group rollup's status comes WITH its colour, because it belongs to a child's palette — the group has
 * no palette that could hold it, and the children may not even share one with each other.
 *
 * The master switch gates the hand-set half only. A derived edge is not a control anyone is offered, so
 * switching hand-set statuses off does not turn off a colour the dial works out for itself.
 */
export function sectionStatusOf(detail: InstanceDetail, sectionKey: string): GeneratedStatus | undefined {
  // KEY-795: a section that says what its edge shows gets exactly that, and nothing else in the chain below.
  const own = sectionColourOf(detail, sectionKey);
  if (own) {
    switch (own.source) {
      case "None":
        return undefined;
      case "HandSet":
        return handSetStatusOf(detail, sectionKey);
      case "Rule": {
        const handSet = own.allowHandSet ? handSetStatusOf(detail, sectionKey) : undefined;
        if (handSet) {
          return handSet;
        }
        return own.statusKey && own.colour
          ? asStatus({ key: own.statusKey, name: own.name, colour: own.colour })
          : undefined;
      }
      case "Busyness": {
        const dayEdge = detail.sections.find((s) => s.key === sectionKey)?.edgeStatusKey;
        return dayEdge ? detail.statusSet.statuses.find((s) => s.key === dayEdge) : undefined;
      }
      default:
        // GroupRollup, and anything newer than this client: the chain below already reads a group's rollup.
        break;
    }
  }
  const handSet = handSetStatusOf(detail, sectionKey);
  if (handSet) {
    return handSet;
  }
  const rolledUp = detail.groupSectionStatuses?.find((s) => s.sectionKey === sectionKey);
  if (rolledUp) {
    // Everything but key/name/colour is unknowable here — the group cannot see the child palette's ordering,
    // its terminal flag or its role gates — and an edge reads none of them: it draws the colour and names the
    // status. The filler values deliberately claim nothing (no position in a palette this status is not in,
    // and no roles, since nobody sets a rolled-up edge by hand).
    return {
      key: rolledUp.statusKey,
      name: rolledUp.statusName,
      colour: rolledUp.statusColour,
      order: 0,
      allowedRoles: [],
      isTerminal: false,
    };
  }
  // KEY-771 part B: a day of a days section coloured from its items carries how busy the whole day is, as a key
  // in the dial's DEFAULT palette (its busyness bands) — not the section axis, which says nothing about busyness.
  const dayEdge = detail.sections.find((s) => s.key === sectionKey)?.edgeStatusKey;
  if (dayEdge) {
    const busy = detail.statusSet.statuses.find((s) => s.key === dayEdge);
    if (busy) {
      return busy;
    }
  }
  const derived = detail.rollup?.sections?.find((s) => s.sectionKey === sectionKey)?.statusKey;
  // The SECTION axis, which is the action points' one unless the dial separates them (KEY-485).
  return derived ? sectionRollupAxisStatuses(detail).find((s) => s.key === derived) : undefined;
}

/** One arc of a split section edge: an action point, and the colour its own worst-of resolved to. */
export interface SectionEdgeSegment {
  segmentKey: string;
  /** Undefined when nothing resolved for this segment — the arc is left unpainted rather than guessed. */
  colour?: string;
  statusName?: string;
}

/**
 * The per-action-point arcs of a section's status edge, or undefined when this section should keep the
 * single arc `sectionStatusOf` gives it (KEY-494).
 *
 * Undefined in three cases, and the second two are the load-bearing ones:
 *
 * 1. The dial has not switched the split on. Off is the default, so nothing changes for a dial nobody
 *    asked to change.
 * 2. A hand-set section status (KEY-479). That is one person's statement about the whole section, so
 *    there is nothing to split — doing so would invent per-segment detail nobody asserted.
 * 3. Nothing resolved on any segment, so a split edge would be an empty ring.
 *
 * A GROUP splits too (KEY-523). It used to be lumped in with case 2 on the grounds that its children had
 * "no per-segment identity here", which stopped being true once each face segment carried its own mapping;
 * the setting was still offered on a group, so it wrote, reloaded and changed nothing. A group's arcs come
 * from `groupSectionEdgeSegments` — the per-action-point grain of the SAME rollup the single arc uses —
 * and a group section is still unsplittable when a KEY-509 rule decided it, or on a Mirror face, both of
 * which the server reports by sending no rows for that section.
 *
 * On either path the arcs read the SECTION's vocabulary, never the segment's displayed status: the ring is
 * a section status at finer grain. Reading the displayed status instead would turn the edge into a second
 * copy of the segment fill, in a different palette from the edge it replaced — visible on a dial whose
 * sections and action points are bound to different checks.
 */
export function sectionEdgeSegmentsOf(
  detail: InstanceDetail,
  sectionKey: string,
): SectionEdgeSegment[] | undefined {
  // KEY-795: a rule section's arcs are worked out on the server, which already applied the section's own split
  // setting — so they are drawn whether or not the dial splits. A hand-set status it allows is one statement about
  // the whole section, so it keeps one arc.
  const own = sectionColourOf(detail, sectionKey);
  if (own?.source === "Rule") {
    if (own.allowHandSet && handSetStatusOf(detail, sectionKey)) {
      return undefined;
    }
    const ruleArcs = (own.segments ?? []).map((a) => ({
      segmentKey: a.segmentKey,
      colour: a.colour ?? undefined,
      statusName: a.name ?? undefined,
    }));
    return ruleArcs.some((a) => a.colour) ? ruleArcs : undefined;
  }
  if (own?.source === "None" || own?.source === "HandSet") {
    return undefined;
  }
  if (!detail.sectionEdgeSplitEnabled) {
    return undefined;
  }
  const handSet =
    detail.sectionStatusEnabled &&
    detail.sectionStatuses?.some((s) => s.sectionKey === sectionKey && !!s.statusKey);
  if (handSet) {
    return undefined;
  }

  const section = detail.sections.find((s) => s.key === sectionKey);
  if (!section) {
    return undefined;
  }

  // A group's edge is rolled up from its children (KEY-483), so its split comes from the server's
  // per-action-point breakdown of that same rollup rather than from this dial's own axis. No rows means
  // the section is not splittable — a rule decided it, or this is a Mirror face — so it keeps one arc.
  if (detail.groupSectionStatuses?.some((s) => s.sectionKey === sectionKey)) {
    const rows = (detail.groupSectionEdgeSegments ?? []).filter((g) => g.sectionKey === sectionKey);
    if (rows.length === 0) {
      return undefined;
    }
    const byKey = new Map(rows.map((r) => [r.segmentKey, r]));
    const groupArcs = section.segments.map((segment) => {
      const row = byKey.get(segment.key);
      return { segmentKey: segment.key, colour: row?.statusColour, statusName: row?.statusName };
    });
    return groupArcs.some((a) => a.colour) ? groupArcs : undefined;
  }

  // KEY-771 part B: a day's times of day each carry their own busyness, in the dial's default palette.
  if (section.segments.some((segment) => segment.edgeStatusKey)) {
    const busyness = detail.statusSet.statuses;
    const dayArcs = section.segments.map((segment) => {
      const status = segment.edgeStatusKey ? busyness.find((s) => s.key === segment.edgeStatusKey) : undefined;
      return { segmentKey: segment.key, colour: status?.colour, statusName: status?.name };
    });
    return dayArcs.some((a) => a.colour) ? dayArcs : undefined;
  }

  const palette = sectionRollupAxisStatuses(detail);
  const arcs = section.segments.map((segment) => {
    const key = detail.rollup?.segments?.find(
      (r) => r.sectionKey === sectionKey && r.segmentKey === segment.key,
    )?.sectionAxisStatusKey;
    const status = key ? palette.find((s) => s.key === key) : undefined;
    return { segmentKey: segment.key, colour: status?.colour, statusName: status?.name };
  });

  return arcs.some((a) => a.colour) ? arcs : undefined;
}

export const lookupStatus = (index: Map<string, string>, sectionKey: string, segmentKey: string) =>
  index.get(segKey(sectionKey, segmentKey));

/** A segment's effective palette: its named override when set, otherwise the definition default. */
export function paletteForSegment(detail: InstanceDetail, segment: { palette?: string | null }): GeneratedStatus[] {
  const override = paletteOverrideForSegment(detail, segment);
  return override ?? detail.statusSet.statuses;
}

/**
 * A segment's EXPLICIT palette override statuses, or `undefined` when it inherits the definition
 * default. Unlike {@link paletteForSegment} (which falls back to the default), this distinguishes an
 * intentional override — used to tint itemised segments only when the author chose a palette (KEY-187).
 */
export function paletteOverrideForSegment(
  detail: InstanceDetail,
  segment: { palette?: string | null },
): GeneratedStatus[] | undefined {
  if (!segment.palette) {
    return undefined;
  }
  return detail.palettes.find((p) => p.name === segment.palette)?.statuses;
}

/** Every status across all palettes, keyed by status key (first definition wins). */
export function unionStatusByKey(detail: InstanceDetail): Map<string, GeneratedStatus> {
  const map = new Map<string, GeneratedStatus>();
  for (const palette of detail.palettes) {
    for (const s of palette.statuses) {
      if (!map.has(s.key)) {
        map.set(s.key, s);
      }
    }
  }
  return map;
}

/**
 * KEY-736: true for an item that is an event read from someone's connected calendar rather than one of the dial's
 * own. Events sit in a day's times of day beside the dial's items but are never part of its completion.
 */
export function isCalendarEvent(item: GeneratedItem): boolean {
  return (item as ResolvedItem).calendar != null;
}

/**
 * Per-segment completion bands + tallies derived on the client, mirroring the server's ItemRollup
 * (src/api/KeyDials.Domain/Instances/ItemRollup.cs — the source of truth for this logic). An item is
 * complete when every gating dimension is at a terminal status (the dimensions marked required, or all of
 * them when none are); a segment is Green when all its items are complete, Red when none are, Amber between.
 * An item whose every gating dimension sits at an N/A (`isExcludedFromRollup`) status drops out of both
 * tallies, so 3 terminal + 1 N/A reads as 3/3.
 *
 * This is a FALLBACK used only when the server withholds its rollup — i.e. for a section/segment-scoped
 * viewer, who is not shown the whole-instance aggregate (InstanceService returns a null `rollup`). The
 * server has already stripped `itemStatuses` and the structure to that viewer's visible segments, so each
 * band here is derived solely from its own segment's visible items: it cannot reveal anything about sections
 * the viewer can't see. Full-visibility callers keep the authoritative server rollup, untouched.
 *
 * Item-less segments are omitted, exactly as the server rollup omits them, so they fall through to the same
 * "no rollup → grey" rendering as under full visibility. A segment whose items are ALL excluded is omitted
 * on the same grounds — it has nothing left to score.
 *
 * Calendar events (KEY-736) are left out before anything is scored, as the server's `CalendarTitles.WithoutEvents`
 * leaves them out of its rollup. They do carry statuses — the server implies "Coming up" for each on a day coloured
 * from its items — so keeping them in would score every appointment as unfinished work for a scoped viewer only
 * (KEY-773). A slot holding nothing but events is therefore omitted, just as it is from the server's rollup.
 */
export function deriveSegmentRollups(detail: InstanceDetail): SegmentRollup[] {
  const dimensions = detail.dimensions ?? [];
  // With no scoring axes there is no completion to roll up: the server itself skips the rollup for a
  // dimensionless itemised dial (so those segments stay grey under full visibility too) — match it exactly.
  if (dimensions.length === 0) {
    return [];
  }
  // The gating dimensions decide completion: those marked required, or all of them when none are (matches
  // the server).
  const required = dimensions.filter((d) => d.requiredForCompletion);
  const gating = required.length > 0 ? required : dimensions;
  const terminalByDimension = new Map(
    gating.map((d) => [d.key, new Set(d.palette.statuses.filter((s) => s.isTerminal).map((s) => s.key))]),
  );
  const excludedByDimension = new Map(
    gating.map((d) => [d.key, new Set(d.palette.statuses.filter((s) => s.isExcludedFromRollup).map((s) => s.key))]),
  );

  // (section, segment, item, dimension) → current status key, over the visible item statuses only.
  const statusByItemDimension = new Map(
    (detail.itemStatuses ?? []).map(
      (s) => [`${s.sectionKey}\0${s.segmentKey}\0${s.itemKey}\0${s.dimensionKey}`, s.statusKey] as const,
    ),
  );
  // Mirrors ItemRollup.DimensionsFor: a scoped dimension only scores the items it names.
  const gatingFor = (sectionKey: string, segmentKey: string, itemKey: string) =>
    gating.filter((d) => itemScopeApplies(d.appliesToItemKeys, sectionKey, segmentKey, itemKey));
  const isDimensionTerminal = (sectionKey: string, segmentKey: string, itemKey: string, dimensionKey: string) => {
    const statusKey = statusByItemDimension.get(`${sectionKey}\0${segmentKey}\0${itemKey}\0${dimensionKey}`);
    return statusKey !== undefined && terminalByDimension.get(dimensionKey)?.has(statusKey) === true;
  };
  const isItemComplete = (sectionKey: string, segmentKey: string, itemKey: string): boolean => {
    const own = gatingFor(sectionKey, segmentKey, itemKey);
    return own.length > 0 && own.every((d) => isDimensionTerminal(sectionKey, segmentKey, itemKey, d.key));
  };
  // Partial credit, mirroring ItemRollup.ItemProgress: the share of gating dimensions already terminal.
  const itemProgress = (sectionKey: string, segmentKey: string, itemKey: string): number => {
    const own = gatingFor(sectionKey, segmentKey, itemKey);
    return own.length === 0
      ? 0
      : own.filter((d) => isDimensionTerminal(sectionKey, segmentKey, itemKey, d.key)).length / own.length;
  };
  // An item no dimension scores leaves both tallies, exactly as ItemRollup.Compute drops it.
  const isItemUnscored = (sectionKey: string, segmentKey: string, itemKey: string): boolean =>
    gatingFor(sectionKey, segmentKey, itemKey).length === 0;
  // "Not applicable" only when EVERY gating dimension says so — N/A on one axis while another is live
  // still leaves the item scoreable (matches ItemRollup.IsItemExcluded).
  const isItemExcluded = (sectionKey: string, segmentKey: string, itemKey: string): boolean => {
    const own = gatingFor(sectionKey, segmentKey, itemKey);
    return own.length > 0 && own.every((d) => {
      const statusKey = statusByItemDimension.get(`${sectionKey}\0${segmentKey}\0${itemKey}\0${d.key}`);
      return statusKey !== undefined && excludedByDimension.get(d.key)?.has(statusKey) === true;
    });
  };

  const rollups: SegmentRollup[] = [];
  for (const section of detail.sections) {
    for (const segment of section.segments) {
      const scorable = segment.items.filter((item) => !isCalendarEvent(item) &&
        !isItemUnscored(section.key, segment.key, item.key) && !isItemExcluded(section.key, segment.key, item.key));
      const total = scorable.length;
      if (total === 0) {
        continue; // the server never emits a rollup entry for an item-less (or all-N/A) segment
      }
      const complete = scorable.filter((item) => isItemComplete(section.key, segment.key, item.key)).length;
      const progress =
        scorable.reduce((sum, item) => sum + itemProgress(section.key, segment.key, item.key), 0) / total;
      rollups.push({
        sectionKey: section.key,
        segmentKey: segment.key,
        band: complete <= 0 ? "Red" : complete >= total ? "Green" : "Amber",
        completeItems: complete,
        totalItems: total,
        progress,
      });
    }
  }
  return rollups;
}

/**
 * The colour a segment's RESOLVED status key maps to, or `undefined` when its rollup resolved no status —
 * which is every segment until display rules can be authored, and any segment whose rule has no opinion.
 * Callers fall back to their existing band colouring, so this is purely additive.
 *
 * Resolved against the segment's EFFECTIVE palette (its override when set, else the definition default),
 * because that is the palette whose statuses a rule names.
 */
export function resolvedStatus(
  detail: InstanceDetail,
  segment: { palette?: string | null },
  rollup: { statusKey?: string | null } | undefined,
): GeneratedStatus | undefined {
  const statusKey = rollup?.statusKey;
  if (!statusKey) {
    return undefined;
  }
  // Two resolvers feed this one field and they answer in different palettes: a display rule resolves into
  // the SEGMENT's palette, the axis rollup (KEY-482) into the AXIS's. Segment first, so nothing about the
  // rules changes; the axis behind it, or a dial whose axis palette differs from its segments' would resolve
  // a status server-side and then quietly fail to colour anything.
  return paletteForSegment(detail, segment).find((s) => s.key === statusKey)
    ?? rollupAxisStatuses(detail).find((s) => s.key === statusKey);
}

/**
 * KEY-736/771: the status a day of a days section shows for one time of day, or `undefined` when `segment` isn't one.
 * The server colours these — by how busy the slot is, or from its items — and sends the answer as the segment's
 * status. The item rollup never knows: calendar events are kept out of it, so a slot holding only events has items
 * but no rollup entry, and reading the rollup painted it grey. `null` is a day slot the server left uncoloured.
 * A section coloured from its items names its colours in its check's palette, not the segment's.
 */
export function daySlotStatus(
  detail: InstanceDetail,
  section: { slotColouring?: { dimensionKey: string } | null },
  segment: { key: string; date?: string | null; palette?: string | null },
  sectionKey: string,
): GeneratedStatus | null | undefined {
  if (!segment.date) {
    return undefined;
  }
  const statusKey = detail.statuses.find((s) => s.sectionKey === sectionKey && s.segmentKey === segment.key)?.statusKey;
  if (!statusKey) {
    return null;
  }
  const checkKey = section.slotColouring?.dimensionKey;
  const palette = checkKey
    ? detail.dimensions?.find((d) => d.key === checkKey)?.palette.statuses ?? []
    : paletteForSegment(detail, segment);
  return palette.find((s) => s.key === statusKey) ?? null;
}

/** The colour of {@link resolvedStatus}, for the callers that only paint with it. */
export function resolvedStatusColour(
  detail: InstanceDetail,
  segment: { palette?: string | null },
  rollup: { statusKey?: string | null } | undefined,
): string | undefined {
  return resolvedStatus(detail, segment, rollup)?.colour;
}

/**
 * Index an itemised instance's per-segment rollups by section+segment.
 *
 * Falls back to {@link deriveSegmentRollups} when the server withholds its rollup — that happens for a
 * section/segment-scoped viewer, and without the fallback every segment would come back unbanded and render
 * grey. `buildDialVM` has always had this fallback; this index did not.
 */
export function rollupIndex(detail: InstanceDetail): Map<string, SegmentRollup> {
  const segments = detail.rollup?.segments ?? (detail.kind === "Itemised" ? deriveSegmentRollups(detail) : []);
  return new Map(segments.map((s) => [segKey(s.sectionKey, s.segmentKey), s]));
}

/** Index an itemised instance's per-segment rollup bands (Red/Amber/Green) by section+segment. */
export function bandIndex(detail: InstanceDetail): Map<string, RollupBand> {
  const index = new Map<string, RollupBand>();
  for (const [key, rollup] of rollupIndex(detail)) {
    index.set(key, rollup.band);
  }
  return index;
}

/**
 * Build a renderer DialModel from an instance.
 *
 * - **Simple** dials colour each segment by its CURRENT status in its OWN palette (segments may
 *   override the default) and let the renderer derive completion from those statuses.
 * - **Itemised** dials colour each segment that HAS items by its server-rolled-up RAG band and feed the
 *   whole-dial completion fraction straight to the hub ring. An item-less segment takes the status path
 *   instead (KEY-259) — it has nothing to roll up, so it carries its own status like a Simple one.
 *
 * The model palette is the union of every palette so the renderer can still resolve status colours;
 * per-segment colours are set explicitly so overrides render correctly even when palettes reuse keys.
 */
export function instanceToDialModel(detail: InstanceDetail): DialModel {
  const palette = [...unionStatusByKey(detail).values()].map((s) => ({
    key: s.key,
    colour: s.colour,
    order: s.order,
    isTerminal: s.isTerminal,
  }));
  const itemised = detail.kind === "Itemised";
  const index = statusIndex(detail);
  const rollups = itemised ? rollupIndex(detail) : null;
  return {
    innerRadius: 120,
    midRadius: 300,
    outerRadius: 372,
    sectionGap: 0.012,
    palette,
    // Itemised dials roll completion up server-side; simple dials derive it from segment statuses.
    completion: itemised ? detail.rollup?.completion ?? 0 : undefined,
    sections: detail.sections.map((section) => ({
      key: section.key,
      label: section.label,
      // KEY-755: the section's own band colour, so the export is the colour the screen is.
      colour: section.colour,
      // The section's own status colour (KEY-479), so the export shows the same edge as the screen.
      edgeColour: sectionStatusOf(detail, section.key)?.colour,
      // ...and its per-segment split (KEY-494), for the same reason: an exported PDF that showed one
      // flat edge where the screen shows a run of arcs would be a different dial.
      edgeSegments: sectionEdgeSegmentsOf(detail, section.key)?.map((a) => ({
        segmentKey: a.segmentKey,
        colour: a.colour,
      })),
      segments: section.segments.map((segment) => {
        // KEY-259: an itemised action point is only coloured by its rollup once it HAS an item; an empty one
        // falls through to the status path below, exactly like a Simple dial's action point. Kept in step with
        // buildDialVM so the two model builders never disagree about the same segment.
        const daySlot = daySlotStatus(detail, section, segment, section.key);
        if (daySlot !== undefined) {
          return { key: segment.key, label: segment.label, colour: daySlot?.colour, itemStatusKeys: daySlot ? [daySlot.key] : [] };
        }
        if (itemised && segment.items.length > 0) {
          const rollup = rollups!.get(segKey(section.key, segment.key));
          const band = rollup?.band;
          // A display rule wins when it resolved a status; otherwise a per-segment palette override tints the
          // segment by completion band (KEY-187), and segments without one keep the default RAG colour.
          const override = paletteOverrideForSegment(detail, segment);
          const colour =
            resolvedStatusColour(detail, segment, rollup) ??
            (band ? (override ? bandPaletteColour(band, override) : bandColour(band)) : undefined);
          return { key: segment.key, label: segment.label, colour, itemStatusKeys: [] };
        }
        const statusKey = lookupStatus(index, section.key, segment.key);
        const colour = statusKey
          ? paletteForSegment(detail, segment).find((s) => s.key === statusKey)?.colour
          : undefined;
        return { key: segment.key, label: segment.label, colour, itemStatusKeys: statusKey ? [statusKey] : [] };
      }),
    })),
  };
}
